import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DmBroadcastStatus,
  DmChannelConfig,
  DmChannelConfigPatch,
  DmCreatorBroadcast,
  DmError,
  DmInsights,
  dmBroadcastList,
  dmBroadcastRetract,
  dmBroadcastSend,
  dmChannelConfigGet,
  dmChannelConfigSet,
  dmImageUpload,
  dmInsights,
  dmVideoBegin,
  dmVideoComplete,
  dmVideoPut,
  dmVideoStatus,
  dmVoiceUpload,
} from "@/services/firebase/artistHub";
import {
  DmComposeSender,
  DmComposeType,
  DM_POLL_QUESTION_MAX,
  buildBroadcastBody,
  codePoints,
  failReasonLabel,
  formatDuration,
  previewName,
  recentMonths,
  senderKindsFor,
  subscriptionDayBars,
  videoFileCheck,
  voiceFileProblem,
} from "@/lib/dm";
import { Md5 } from "@/lib/md5";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { useArtistConfirm } from "./ArtistConfirm";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const fmtDay = (ms: number) => new Date(ms).toLocaleDateString("zh-TW", { month: "numeric", day: "numeric" });
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
// <input type="date"> → end of that local day (epoch ms).
const endOfDay = (value: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split("-").map(Number);
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime();
};
const dateValue = (ms: number | null) => {
  if (!ms) return "";
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
// <input type="datetime-local"> (local time) → epoch ms.
const localMs = (value: string): number | null => (value ? new Date(value).getTime() : null);
const newRequestId = () => crypto.randomUUID();
const readBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(new Error("讀取檔案失敗"));
    reader.readAsDataURL(file);
  });
/** Duration from the browser's media metadata (ms), or null when it cannot be decoded. */
const readDuration = (file: File, kind: "audio" | "video") =>
  new Promise<number | null>((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement(kind);
    const done = (ms: number | null) => {
      URL.revokeObjectURL(url);
      resolve(ms);
    };
    el.preload = "metadata";
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? Math.round(el.duration * 1000) : null);
    el.onerror = () => done(null);
    setTimeout(() => done(null), 10000);
    el.src = url;
  });
/** Streams the file through MD5 in 4 MiB slices (videoBegin needs the hex md5). */
async function fileMd5(file: File): Promise<string> {
  const md5 = new Md5();
  const step = 4 * 1024 * 1024;
  for (let at = 0; at < file.size; at += step) md5.update(new Uint8Array(await file.slice(at, at + step).arrayBuffer()));
  return md5.hex();
}
/** One voice clip (broadcast or welcome): duration check → mediaUploadAudio. */
async function uploadVoice(artistId: string, file: File): Promise<{ assetId: string; durationMs: number }> {
  const problem = voiceFileProblem(file, await readDuration(file, "audio"));
  if (problem) throw new Error(problem);
  const assetId = crypto.randomUUID();
  const r = await dmVoiceUpload(artistId, assetId, await readBase64(file));
  return { assetId: r.assetId, durationMs: r.durationMs };
}

const statusBadge: Record<DmChannelConfig["status"], { label: string; variant: "success" | "warning" | "danger" | "neutral" }> = {
  active: { label: "開放中", variant: "success" },
  paused: { label: "停更中", variant: "warning" },
  suspended: { label: "營運停用", variant: "danger" },
  closed: { label: "已關閉", variant: "danger" },
};
const broadcastStatusLabels: Record<DmBroadcastStatus, string> = {
  sent: "已發送",
  scheduled: "排程中",
  processing: "影片處理中",
  failed: "發送失敗",
  retracted: "已收回",
  cancelled: "已取消",
};
const broadcastStatusVariant = (s: DmBroadcastStatus) =>
  s === "sent" ? "success" : s === "scheduled" || s === "processing" ? "info" : s === "failed" ? "danger" : "neutral";
const cancelReasonLabels: Record<string, string> = {
  channel_disabled: "到點時私訊開關已關閉",
  channel_suspended: "到點時聊天室已被停用",
  channel_closed: "到點時聊天室已關閉",
  expired: "逾期 24 小時未送出",
};
const typeLabels: Record<string, string> = { text: "文字", image: "圖片", poll: "投票", voice: "語音", video: "影片" };
const roleLabels = { artist: "藝人本人", editor: "小編", staff: "工作人員" };

/**
 * C-01: 鐵粉私訊設定與廣播（contract v2.2 §1d 創作者端）. artist / editor / staff only (reviewers get 403).
 * Loads lazily so the actor page does not hit artistHub until someone opens it.
 */
export default function ArtistDmPanel({ artistId, previewName: fanName = "小美" }: { artistId: string; previewName?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>鐵粉私訊</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-sm text-muted-foreground">
          藝人對鐵粉的一對多廣播；每位鐵粉只看到自己與藝人的對話，回覆只有藝人團隊看得到。藝人本人可在 App 開啟私訊；CMS 由工作人員或藝人角色操作。
        </p>
        {!open ? (
          <Button variant="outline" onClick={() => setOpen(true)}>
            開啟私訊管理
          </Button>
        ) : (
          <DmPanelBody artistId={artistId} fanName={fanName} />
        )}
      </CardContent>
    </Card>
  );
}

function DmPanelBody({ artistId, fanName }: { artistId: string; fanName: string }) {
  const configKey = ["dm-config", artistId];
  const config = useQuery({ queryKey: configKey, queryFn: () => dmChannelConfigGet(artistId), retry: false });
  if (config.isPending) return <p>讀取私訊設定…</p>;
  if (config.isError) {
    const e = config.error;
    const hint =
      e instanceof DmError && e.code === "DM_DISABLED"
        ? "私訊尚未對此藝人開放：請確認遠端開關 artist_dm_enabled 為 true，且藝人在 artist_dm_allowlist 試點名單內（空白＝全部）。"
        : e instanceof DmError && e.status === 403
          ? "此帳號不是這位藝人的團隊成員（藝人本人、小編）或工作人員，無法管理私訊。"
          : null;
    return (
      <div className="space-y-2">
        <p role="alert" className="text-destructive">
          {errorText(e)}
        </p>
        {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
        <Button variant="outline" size="sm" onClick={() => void config.refetch()}>
          重試
        </Button>
      </div>
    );
  }
  return (
    <>
      <ChannelSettings artistId={artistId} config={config.data} fanName={fanName} />
      <DeliveryDashboard artistId={artistId} config={config.data} />
      <Broadcasts artistId={artistId} config={config.data} fanName={fanName} />
    </>
  );
}

/* ---------- 開關／歡迎訊息／靜音時段／停更公告 ---------- */

function ChannelSettings({ artistId, config, fanName }: { artistId: string; config: DmChannelConfig; fanName: string }) {
  const client = useQueryClient();
  const [welcome, setWelcome] = useState(config.welcome?.text ?? "");
  const [welcomeVoice, setWelcomeVoice] = useState<{ assetId: string; durationMs: number | null } | null>(
    config.welcome?.media ? { assetId: config.welcome.media.assetId, durationMs: config.welcome.media.durationMs } : null,
  );
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const pickWelcomeVoice = async (file: File | undefined) => {
    if (!file) return;
    setVoiceError("");
    setVoiceBusy(true);
    try {
      setWelcomeVoice(await uploadVoice(artistId, file));
    } catch (e) {
      setVoiceError(errorText(e));
    } finally {
      setVoiceBusy(false);
    }
  };
  const [quietStart, setQuietStart] = useState(config.quietHours?.start ?? "22:00");
  const [quietEnd, setQuietEnd] = useState(config.quietHours?.end ?? "09:00");
  const [pauseStart, setPauseStart] = useState(dateValue(Date.now()));
  const [pauseEnd, setPauseEnd] = useState(dateValue(config.pausedUntil));
  const [pauseNote, setPauseNote] = useState(config.pauseNote ?? "");
  const [notice, setNotice] = useState("");
  const save = useMutation({
    mutationFn: ({ patch }: { patch: DmChannelConfigPatch; label: string }) => dmChannelConfigSet(artistId, patch),
    onSuccess: (next, { label }) => {
      client.setQueryData(["dm-config", artistId], next);
      setNotice(label);
      void client.invalidateQueries({ queryKey: ["artist-audit", artistId] });
    },
  });
  const run = (patch: DmChannelConfigPatch, label: string) => {
    setNotice("");
    save.mutate({ patch, label });
  };
  const badge = statusBadge[config.status] ?? statusBadge.active;
  const welcomeLen = codePoints(welcome);
  const frozen = config.status === "suspended" || config.status === "closed";
  const applyPauseTemplate = () => {
    const s = endOfDay(pauseStart);
    const e = endOfDay(pauseEnd);
    if (s && e) setPauseNote(`${fmtDay(s)}–${fmtDay(e)} 暫停私訊，期間訊息照常保留，回來再跟大家聊！`);
  };
  return (
    <section className="space-y-5" aria-label="私訊設定">
      <div className="flex flex-wrap items-center gap-3">
        <Switch
          id={`dm-enabled-${artistId}`}
          checked={config.enabled}
          disabled={!config.canToggle || save.isPending}
          onCheckedChange={(enabled) => run({ enabled }, enabled ? "已開啟鐵粉私訊" : "已關閉鐵粉私訊")}
        />
        <Label htmlFor={`dm-enabled-${artistId}`}>開放鐵粉私訊</Label>
        <Badge variant={badge.variant}>{badge.label}</Badge>
        <span className="text-sm text-muted-foreground">目前身分：{roleLabels[config.role] ?? config.role}</span>
      </div>
      <p className="text-sm text-muted-foreground">
        藝人本人可在 App 開啟；CMS 由工作人員（staff）或藝人（artist）角色操作，小編只能改歡迎訊息、靜音時段與停更公告。
        {!config.canToggle && "（你目前是小編，無法切換開關）"}
      </p>
      {frozen && (
        <p role="status" className="rounded border border-destructive p-2 text-sm">
          營運已{config.status === "closed" ? "關閉" : "停用"}此聊天室：粉絲與藝人都只能讀不能寫。{config.statusReason ? `原因：${config.statusReason}` : ""}
        </p>
      )}

      <div className="space-y-2">
        <Label htmlFor={`dm-welcome-${artistId}`}>歡迎訊息（新鐵粉第一次進聊天室時收到，可用 {"{name}"} 代入暱稱）</Label>
        <Textarea id={`dm-welcome-${artistId}`} rows={3} value={welcome} disabled={save.isPending} onChange={(e) => setWelcome(e.target.value)} />
        <p className={`text-xs ${welcomeLen > 1000 ? "text-destructive" : "text-muted-foreground"}`}>
          {welcomeLen}／1,000 字 · 預覽：{welcome ? previewName(welcome, fanName) : "（未設定）"}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <Label htmlFor={`dm-welcome-voice-${artistId}`} className="text-sm">
            語音歡迎（選填，m4a ≤60 秒、≤2MB）
          </Label>
          <Input
            id={`dm-welcome-voice-${artistId}`}
            type="file"
            accept=".m4a,.aac,audio/mp4,audio/x-m4a,audio/aac"
            className="w-auto"
            disabled={voiceBusy || save.isPending}
            onChange={(e) => {
              void pickWelcomeVoice(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {voiceBusy && <span className="text-xs">上傳中…</span>}
          {welcomeVoice && !voiceBusy && (
            <span className="inline-flex items-center gap-2 text-xs">
              語音 {formatDuration(welcomeVoice.durationMs)}
              {config.welcome?.media?.assetId === welcomeVoice.assetId ? "（已儲存）" : "（尚未儲存）"}
              <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => setWelcomeVoice(null)}>
                移除語音
              </Button>
            </span>
          )}
        </div>
        {voiceError && (
          <p role="alert" className="text-xs text-destructive">
            {voiceError}
          </p>
        )}
        <p className="text-xs text-muted-foreground">有語音時文字可留白。歡迎訊息不計入每月交付。移除語音後需按「儲存歡迎訊息」才會生效。</p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={save.isPending || voiceBusy || (!welcome.trim() && !welcomeVoice) || welcomeLen > 1000}
            onClick={() =>
              run(
                { welcome: { text: welcome.trim(), ...(welcomeVoice ? { mediaRef: { assetId: welcomeVoice.assetId } } : {}) } },
                "已儲存歡迎訊息",
              )
            }
          >
            儲存歡迎訊息
          </Button>
          {config.welcome && (
            <Button
              size="sm"
              variant="outline"
              disabled={save.isPending}
              onClick={() => {
                setWelcomeVoice(null);
                run({ welcome: null }, "已清除歡迎訊息");
              }}
            >
              清除
            </Button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">靜音時段（台北時間；訊息照常送達，推播延後到時段結束）</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor={`dm-quiet-start-${artistId}`}>開始</Label>
            <Input id={`dm-quiet-start-${artistId}`} type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value)} />
          </div>
          <div>
            <Label htmlFor={`dm-quiet-end-${artistId}`}>結束</Label>
            <Input id={`dm-quiet-end-${artistId}`} type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value)} />
          </div>
          <Button
            size="sm"
            disabled={save.isPending || !quietStart || !quietEnd || quietStart === quietEnd}
            onClick={() => run({ quietHours: { start: quietStart, end: quietEnd } }, "已儲存靜音時段")}
          >
            儲存靜音時段
          </Button>
          {config.quietHours && (
            <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => run({ quietHours: null }, "已取消靜音時段")}>
              取消靜音時段
            </Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          目前：{config.quietHours ? `${config.quietHours.start}–${config.quietHours.end}` : "未設定"}
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-medium">停更公告（期間粉絲仍可讀可回，加入頁顯示暫停公告）</p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label htmlFor={`dm-pause-start-${artistId}`}>開始日（只用於公告文字）</Label>
            <Input id={`dm-pause-start-${artistId}`} type="date" value={pauseStart} onChange={(e) => setPauseStart(e.target.value)} />
          </div>
          <div>
            <Label htmlFor={`dm-pause-end-${artistId}`}>結束日（90 天內）</Label>
            <Input id={`dm-pause-end-${artistId}`} type="date" value={pauseEnd} onChange={(e) => setPauseEnd(e.target.value)} />
          </div>
          <Button size="sm" variant="outline" disabled={!pauseStart || !pauseEnd} onClick={applyPauseTemplate}>
            套用公告範本
          </Button>
        </div>
        <Textarea rows={2} maxLength={200} value={pauseNote} placeholder="例：10/20–10/27 拍戲暫停私訊" onChange={(e) => setPauseNote(e.target.value)} />
        <p className="text-xs text-muted-foreground">
          儲存後立即進入停更狀態，到結束日 23:59 自動恢復。後端只記錄結束時間，開始日請寫在公告文字中。目前：
          {config.pausedUntil ? `停更至 ${fmt(config.pausedUntil)}` : "未停更"}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            disabled={save.isPending || !endOfDay(pauseEnd)}
            onClick={() => {
              const until = endOfDay(pauseEnd);
              if (until) run({ pausedUntil: until, pauseNote: pauseNote.trim() }, "已發布停更公告");
            }}
          >
            發布停更公告
          </Button>
          {config.pausedUntil && (
            <Button size="sm" variant="outline" disabled={save.isPending} onClick={() => run({ pausedUntil: null, pauseNote: null }, "已結束停更")}>
              提前結束停更
            </Button>
          )}
        </div>
      </div>

      {save.isError && (
        <p role="alert" className="text-destructive">
          {errorText(save.error)}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
    </section>
  );
}

/* ---------- 交付儀表 ---------- */

function DeliveryDashboard({ artistId, config }: { artistId: string; config: DmChannelConfig }) {
  const months = recentMonths(13);
  const [month, setMonth] = useState(config.stats.month || months[0]);
  const insights = useQuery({ queryKey: ["dm-insights", artistId, month], queryFn: () => dmInsights(artistId, month), retry: false });
  const d = insights.data;
  const cell = (label: string, value: string, ok?: boolean) => (
    <div className={`rounded border p-3 ${ok === false ? "border-destructive" : ""}`}>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-lg font-semibold ${ok === false ? "text-destructive" : ""}`}>{value}</p>
    </div>
  );
  return (
    <section className="space-y-3 border-t pt-5" aria-label="交付儀表">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="text-base font-semibold">交付儀表</h3>
        <Label htmlFor={`dm-month-${artistId}`} className="sr-only">
          月份
        </Label>
        <select
          id={`dm-month-${artistId}`}
          className="rounded border bg-background px-2 py-1 text-sm"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
        >
          {months.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
        <Button size="sm" variant="outline" disabled={insights.isFetching} onClick={() => void insights.refetch()}>
          重新整理
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        只計藝人本人發送（小編、歡迎訊息不計）；週以台北時間週一起算。最低交付：每月 ≥{config.rules.delivery.minMonthly} 則、分布 ≥
        {config.rules.delivery.minWeeks} 週、語音或影片 ≥{config.rules.delivery.minVoiceOrVideo} 則。
      </p>
      {insights.isPending && <p>讀取交付資料…</p>}
      {insights.isError && (
        <p role="alert" className="text-destructive">
          {errorText(insights.error)}
        </p>
      )}
      {d && (
        <>
          {!d.met && (
            <p role="status" className="rounded border border-destructive p-2 text-sm text-destructive">
              {d.month} 尚未達到最低交付門檻。
            </p>
          )}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            {cell("本月已發（本人）／最低", `${d.byArtist}／${d.delivery.minMonthly}`, d.checks.monthly)}
            {cell("涵蓋週數／最低", `${d.weeksCovered}／${d.delivery.minWeeks}`, d.checks.weeks)}
            {cell("語音或影片／最低", `${d.voiceOrVideo}／${d.delivery.minVoiceOrVideo}`, d.checks.voiceOrVideo)}
            {cell(
              "距上次本人發送",
              d.daysSinceLastArtistBroadcast == null ? "尚未發送" : `${d.daysSinceLastArtistBroadcast} 天`,
              d.daysSinceLastArtistBroadcast == null ? undefined : d.daysSinceLastArtistBroadcast <= 7,
            )}
            {cell("回覆率", d.replyRate == null ? "—" : `${(d.replyRate * 100).toFixed(1)}%`)}
            {cell("回覆數", `${d.replyCount}`)}
            {cell("愛心數", `${d.heartCount}`)}
            {cell("聊天室數／小編則數", `${d.threads}／${d.team}`)}
          </div>
          <SubscriptionDays data={d.subscriptionDays} />
          <p className="text-xs text-muted-foreground">
            資料時間 {fmt(d.asOf)}；上次本人發送 {fmt(d.lastArtistBroadcastAt)}。
          </p>
        </>
      )}
    </section>
  );
}

/** dmInsights.subscriptionDays: one-series horizontal bars (current fans, not the selected month). */
function SubscriptionDays({ data }: { data: DmInsights["subscriptionDays"] }) {
  if (!data) return <p className="text-xs text-muted-foreground">訂閱天數分布：後端尚未回傳（需 B-33 部署）。</p>;
  const bars = subscriptionDayBars(data.buckets);
  return (
    <figure className="space-y-2" aria-label="鐵粉訂閱天數分布">
      <figcaption className="text-sm font-medium">
        目前鐵粉的連續訂閱天數（共 {data.total.toLocaleString()} 人{data.truncated ? "，超過 20,000 人僅統計前 20,000" : ""}）
      </figcaption>
      <table className="w-full text-sm">
        <thead className="sr-only">
          <tr>
            <th>連續天數</th>
            <th>人數</th>
            <th>占比</th>
          </tr>
        </thead>
        <tbody>
          {bars.map((b) => (
            <tr key={b.key} title={`${b.label}：${b.count.toLocaleString()} 人（${(b.share * 100).toFixed(1)}%）`}>
              <th scope="row" className="w-28 py-1 pr-2 text-left font-normal text-muted-foreground">
                {b.label}
              </th>
              <td className="py-1">
                <div className="h-3 w-full">
                  <div className="h-3 rounded-r bg-primary" style={{ width: `${b.width * 100}%`, minWidth: b.count ? 2 : 0 }} />
                </div>
              </td>
              <td className="w-28 py-1 pl-2 text-right tabular-nums">
                {b.count.toLocaleString()}（{(b.share * 100).toFixed(0)}%）
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/* ---------- 廣播列表與發送 ---------- */

const listFilters: { value: DmBroadcastStatus | ""; label: string }[] = [
  { value: "", label: "全部" },
  { value: "sent", label: "已發送" },
  { value: "scheduled", label: "排程中" },
  { value: "processing", label: "影片處理中" },
  { value: "failed", label: "發送失敗" },
  { value: "retracted", label: "已收回" },
  { value: "cancelled", label: "已取消" },
];

function Broadcasts({ artistId, config, fanName }: { artistId: string; config: DmChannelConfig; fanName: string }) {
  const { confirm, dialog } = useArtistConfirm();
  const client = useQueryClient();
  const [status, setStatus] = useState<DmBroadcastStatus | "">("");
  const [pages, setPages] = useState<(string | null)[]>([null]);
  const cursor = pages[pages.length - 1];
  const list = useQuery({
    queryKey: ["dm-broadcasts", artistId, status, cursor],
    queryFn: () => dmBroadcastList(artistId, { status: status || null, cursor }),
    retry: false,
  });
  const [notice, setNotice] = useState("");
  const retract = useMutation({
    mutationFn: (b: DmCreatorBroadcast) => dmBroadcastRetract(artistId, b.broadcastId),
    onSuccess: ({ broadcast }) => {
      setNotice(broadcast.status === "cancelled" ? "已取消發送" : "已收回，粉絲端顯示「此訊息已收回」");
      void client.invalidateQueries({ queryKey: ["dm-broadcasts", artistId] });
    },
  });
  const items = list.data?.items ?? [];
  const canRetract = (b: DmCreatorBroadcast) =>
    (b.status === "sent" || b.status === "scheduled" || b.status === "processing") && !(config.role === "editor" && b.senderKind === "artist");
  const pendingSend = (b: DmCreatorBroadcast) => b.status === "scheduled" || b.status === "processing";
  return (
    <section className="space-y-4 border-t pt-5" aria-label="廣播">
      <h3 className="text-base font-semibold">廣播</h3>
      <Composer
        artistId={artistId}
        config={config}
        fanName={fanName}
        onSent={(msg) => {
          setNotice(msg);
          setPages([null]);
          void client.invalidateQueries({ queryKey: ["dm-broadcasts", artistId] });
          void client.invalidateQueries({ queryKey: ["dm-config", artistId] });
          void client.invalidateQueries({ queryKey: ["dm-insights", artistId] });
        }}
      />
      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="依狀態篩選廣播">
        {listFilters.map((f) => (
          <Button
            key={f.value || "all"}
            size="sm"
            variant={status === f.value ? "default" : "outline"}
            aria-pressed={status === f.value}
            onClick={() => {
              setStatus(f.value);
              setPages([null]);
            }}
          >
            {f.label}
          </Button>
        ))}
        <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => void list.refetch()}>
          重新整理
        </Button>
      </div>
      {(retract.isError || list.isError) && (
        <p role="alert" className="text-destructive">
          {errorText(retract.error ?? list.error)}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {list.isPending && <p>讀取廣播…</p>}
      {list.data && items.length === 0 && <p className="text-sm text-muted-foreground">沒有符合條件的廣播。</p>}
      {items.length > 0 && (
        <ul className="divide-y">
          {items.map((b) => (
            <li key={b.broadcastId} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <Badge variant={broadcastStatusVariant(b.status)}>{broadcastStatusLabels[b.status] ?? b.status}</Badge>
                  <span>{typeLabels[b.type] ?? b.type}</span>
                  <span>{b.senderKind === "artist" ? "藝人本人" : "小編"}</span>
                  <span>
                    {b.status === "scheduled"
                      ? `排程 ${fmt(b.scheduledAt)}`
                      : b.status === "processing"
                        ? `建立於 ${fmt(b.createdAt)}${b.scheduledAt ? ` · 排程 ${fmt(b.scheduledAt)}` : ""}`
                        : b.status === "failed"
                          ? `建立於 ${fmt(b.createdAt)}`
                          : b.status === "cancelled"
                            ? `取消於 ${fmt(b.cancelledAt)}`
                            : `發送 ${fmt(b.sentAt)}`}
                  </span>
                  {b.status === "retracted" && <span>收回於 {fmt(b.retractedAt)}</span>}
                </p>
                {b.status === "processing" && (
                  <p className="text-xs text-muted-foreground">影片轉檔完成後由系統自動送出（粉絲目前看不到）；24 小時內未完成會標為失敗。</p>
                )}
                {b.status === "failed" && <p className="text-xs text-destructive">未送出：{failReasonLabel(b.failReason)}。請重新上傳後再發送。</p>}
                {b.status === "cancelled" && b.cancelReason && (
                  <p className="text-xs text-muted-foreground">{cancelReasonLabels[b.cancelReason] ?? b.cancelReason}</p>
                )}
                <p className="whitespace-pre-wrap break-words">{b.text ?? "（已收回）"}</p>
                {b.media.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {b.media.map((m) =>
                      m.kind === "voice" ? (
                        m.url ? (
                          <audio key={m.assetId} controls preload="none" src={m.url} className="h-8" aria-label={`語音 ${formatDuration(m.durationMs)}`} />
                        ) : (
                          <span key={m.assetId} className="rounded bg-muted px-2 py-1 text-xs">
                            語音 {formatDuration(m.durationMs)}
                          </span>
                        )
                      ) : m.kind === "video" ? (
                        <span key={m.assetId} className="rounded bg-muted px-2 py-1 text-xs">
                          影片 {formatDuration(m.durationMs)}
                          {m.width && m.height ? ` · ${m.width}×${m.height}` : ""}
                        </span>
                      ) : m.url ? (
                        <img key={m.assetId} src={m.url} alt="" className="h-16 w-16 rounded object-cover" />
                      ) : (
                        <span key={m.assetId} className="flex h-16 w-16 items-center justify-center rounded bg-muted text-xs">
                          圖片
                        </span>
                      ),
                    )}
                  </div>
                )}
                {b.poll && (
                  <ul className="text-sm">
                    {b.poll.question && <li className="font-medium">{b.poll.question}</li>}
                    {b.poll.options.map((o) => (
                      <li key={o.id}>
                        {o.text}：{o.count} 票
                      </li>
                    ))}
                    <li className="text-xs text-muted-foreground">
                      共 {b.poll.totalVotes} 票 · {b.poll.closed ? "已截止" : b.poll.closesAt ? `截止 ${fmt(b.poll.closesAt)}` : "不限期"}
                    </li>
                  </ul>
                )}
                <p className="text-xs text-muted-foreground">
                  回覆 {b.replyCount} · 愛心 {b.heartCount}
                  {b.fanout ? ` · 送達 ${b.fanout.threads} 個聊天室${b.fanout.status === "done" ? "" : "（送達中）"}` : ""}
                </p>
              </div>
              {canRetract(b) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={retract.isPending}
                  onClick={() => {
                    const pending = pendingSend(b);
                    confirm({
                      title: pending ? "取消尚未送出的訊息" : "收回訊息",
                      message: pending ? "確定取消這則尚未送出的訊息？" : "確定收回這則訊息？粉絲端會顯示「此訊息已收回」，統計不回沖。",
                      confirmText: pending ? "取消發送" : "收回",
                      danger: true,
                      onConfirm: () => retract.mutate(b),
                    });
                  }}
                >
                  {b.status === "scheduled" ? "取消排程" : b.status === "processing" ? "取消發送" : "收回"}
                </Button>
              )}
              {config.role === "editor" && b.senderKind === "artist" && b.status === "sent" && (
                <span className="text-xs text-muted-foreground">本人訊息需由藝人收回</span>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        {pages.length > 1 && (
          <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages([null])}>
            回到最新
          </Button>
        )}
        {list.data?.nextCursor && (
          <Button size="sm" variant="outline" disabled={list.isFetching} onClick={() => setPages((p) => [...p, list.data?.nextCursor ?? null])}>
            較舊的廣播
          </Button>
        )}
      </div>
      {dialog}
    </section>
  );
}

interface Upload {
  assetId: string;
  previewUrl: string;
  status: "uploading" | "ready" | "failed";
  error?: string;
}
/** The single voice / video attachment of a voice or video broadcast. */
interface Clip {
  token: string;
  kind: "voice" | "video";
  assetId: string;
  name: string;
  durationMs: number | null;
  status: "uploading" | "ready" | "failed";
  step: string;
  error?: string;
  /** artistMedia status for video (processing → ready); a broadcast sent before "ready" waits in "processing". */
  videoStatus?: string;
}
const videoStatusLabels: Record<string, string> = {
  uploaded: "已上傳，等待轉檔",
  processing: "轉檔中",
  processed: "轉檔完成，準備中",
  ready: "可播放",
  failed: "轉檔失敗",
};

function Composer({
  artistId,
  config,
  fanName,
  onSent,
}: {
  artistId: string;
  config: DmChannelConfig;
  fanName: string;
  onSent: (message: string) => void;
}) {
  const { confirm, dialog } = useArtistConfirm();
  const [type, setType] = useState<DmComposeType>("text");
  const [text, setText] = useState("");
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [clip, setClip] = useState<Clip | null>(null);
  const [options, setOptions] = useState<string[]>(["", ""]);
  const [question, setQuestion] = useState("");
  const [closesAt, setClosesAt] = useState("");
  const [schedule, setSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [formError, setFormError] = useState("");
  // Same requestId on retry (timeout / network) → backend replays instead of sending twice.
  const requestId = useRef(newRequestId());
  const isStaff = config.role === "staff";
  // v2.4: the artist role picks 本人／小編; editor and staff are always 小編.
  const senderOptions = senderKindsFor(config.role);
  const [senderChoice, setSenderChoice] = useState<DmComposeSender>(senderOptions[0]);
  const senderKind: DmComposeSender = senderOptions.includes(senderChoice) ? senderChoice : "team";
  const clipBusy = clip?.status === "uploading";

  async function pickClip(kind: "voice" | "video", file: File | undefined) {
    if (!file) return;
    setFormError("");
    const token = crypto.randomUUID();
    const update = (patch: Partial<Clip>) => setClip((c) => (c && c.token === token ? { ...c, ...patch } : c));
    setClip({ token, kind, assetId: "", name: file.name, durationMs: null, status: "uploading", step: "檢查檔案…" });
    try {
      if (kind === "voice") {
        update({ step: "上傳語音…" });
        const r = await uploadVoice(artistId, file);
        update({ assetId: r.assetId, durationMs: r.durationMs, status: "ready", step: "" });
        return;
      }
      const durationMs = await readDuration(file, "video");
      const check = videoFileCheck(file, durationMs);
      if ("problem" in check) throw new Error(check.problem);
      update({ durationMs, step: "計算檔案校驗碼…" });
      const md5 = await fileMd5(file);
      const assetId = crypto.randomUUID();
      update({ assetId, step: "建立上傳…" });
      const begun = await dmVideoBegin(artistId, assetId, { bytes: file.size, md5, mime: check.mime });
      if (begun.status === "uploading" || begun.status === "allocating") {
        if (!begun.uploadUrl) throw new Error("後端沒有回傳上傳連結，請重試");
        update({ step: `上傳影片（${(file.size / 1048576).toFixed(1)}MB）…` });
        await dmVideoPut(begun.uploadUrl, file);
      }
      update({ step: "確認上傳…" });
      const done = await dmVideoComplete(artistId, assetId);
      if (done.status === "failed") throw new Error("影片處理失敗，請重新上傳");
      update({ status: "ready", step: "", videoStatus: done.status });
    } catch (e) {
      update({ status: "failed", error: errorText(e), step: "" });
    }
  }
  async function refreshClip() {
    if (!clip || clip.kind !== "video" || !clip.assetId) return;
    try {
      const s = await dmVideoStatus(artistId, clip.assetId);
      setClip((c) => (c && c.assetId === s.assetId ? { ...c, videoStatus: s.status, durationMs: s.durationMs ?? c.durationMs } : c));
    } catch (e) {
      setFormError(errorText(e));
    }
  }
  const uploadsRef = useRef(uploads);
  uploadsRef.current = uploads;
  useEffect(() => () => uploadsRef.current.forEach((u) => URL.revokeObjectURL(u.previewUrl)), []);
  const max = config.rules.imagesMax || 4;
  const textMax = config.rules.broadcastTextMax || 1000;

  async function addFiles(files: FileList | null) {
    if (!files) return;
    const room = max - uploads.length;
    const picked = [...files].slice(0, Math.max(0, room));
    if (files.length > room) setFormError(`圖片最多 ${max} 張`);
    for (const file of picked) {
      if (file.size > 8 * 1024 * 1024) {
        setFormError(`「${file.name}」超過 8MB`);
        continue;
      }
      const upload: Upload = { assetId: crypto.randomUUID(), previewUrl: URL.createObjectURL(file), status: "uploading" };
      setUploads((u) => [...u, upload]);
      try {
        await dmImageUpload(artistId, upload.assetId, await readBase64(file));
        setUploads((u) => u.map((x) => (x.assetId === upload.assetId ? { ...x, status: "ready" } : x)));
      } catch (e) {
        setUploads((u) => u.map((x) => (x.assetId === upload.assetId ? { ...x, status: "failed", error: errorText(e) } : x)));
      }
    }
  }
  const removeUpload = (assetId: string) =>
    setUploads((u) => {
      const gone = u.find((x) => x.assetId === assetId);
      if (gone) URL.revokeObjectURL(gone.previewUrl);
      return u.filter((x) => x.assetId !== assetId);
    });

  const send = useMutation({
    mutationFn: () => {
      if (type === "image" && uploads.some((u) => u.status !== "ready")) throw new Error("還有圖片尚未上傳完成或上傳失敗，請移除後再送出");
      const media = type === "voice" || type === "video";
      if (media && (!clip || clip.kind !== type || clip.status !== "ready")) throw new Error(type === "voice" ? "請先上傳語音" : "請先上傳影片");
      const body = buildBroadcastBody({
        type,
        text,
        assetIds: media ? [clip?.assetId ?? ""] : uploads.map((u) => u.assetId),
        pollOptions: options,
        pollQuestion: question,
        pollClosesAt: localMs(closesAt),
        scheduledAt: schedule ? localMs(scheduledAt) : null,
        role: config.role,
        senderKind,
      });
      return dmBroadcastSend(artistId, requestId.current, body);
    },
    onSuccess: (r) => {
      requestId.current = newRequestId();
      setText("");
      uploads.forEach((u) => URL.revokeObjectURL(u.previewUrl));
      setUploads([]);
      setClip(null);
      setOptions(["", ""]);
      setQuestion("");
      setClosesAt("");
      setSchedule(false);
      setScheduledAt("");
      setFormError("");
      onSent(
        r.broadcast.status === "processing"
          ? "影片仍在轉檔，訊息會在處理完成後自動送出（列表顯示「影片處理中」）"
          : r.broadcast.status === "scheduled"
            ? `已排程於 ${fmt(r.broadcast.scheduledAt)} 發送`
            : `已發送${r.fanout ? `，送達 ${r.fanout.threads} 個聊天室${r.fanout.status === "partial" || r.fanout.status === "continuing" ? "（正在送達所有鐵粉，系統會續送）" : ""}` : ""}`,
      );
    },
  });
  const textLen = codePoints(text);
  const disabled = send.isPending || clipBusy || config.status === "suspended" || config.status === "closed" || !config.enabled;
  const canPickArtist = senderOptions.includes("artist");
  return (
    <div className="space-y-3 rounded border p-4">
      <div className="flex flex-wrap gap-2" role="group" aria-label="訊息類型">
        {(["text", "image", "voice", "video", "poll"] as const).map((t) => (
          <Button
            key={t}
            size="sm"
            variant={type === t ? "default" : "outline"}
            aria-pressed={type === t}
            disabled={send.isPending || clipBusy}
            onClick={() => {
              setType(t);
              if (clip && clip.kind !== t) setClip(null);
            }}
          >
            {typeLabels[t]}
          </Button>
        ))}
      </div>

      <div className="space-y-1" role="radiogroup" aria-label="發送身分">
        <p className="text-sm font-medium">發送身分</p>
        <label className="mr-4 inline-flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={`dm-sender-${artistId}`}
            value="artist"
            checked={senderKind === "artist"}
            disabled={!canPickArtist || send.isPending}
            onChange={() => setSenderChoice("artist")}
          />
          藝人本人（計入每月交付）
        </label>
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="radio"
            name={`dm-sender-${artistId}`}
            value="team"
            checked={senderKind === "team"}
            disabled={!canPickArtist || send.isPending}
            onChange={() => setSenderChoice("team")}
          />
          小編（粉絲端顯示「小編」標籤，不計入交付）
        </label>
        {canPickArtist && senderKind === "team" && (
          <p className="text-xs text-muted-foreground">你是藝人本人，這則會以「小編」身分送出，不計入交付。</p>
        )}
        {isStaff && (
          <p className="text-xs text-amber-700">
            以本人身分發送需藝人授權：工作人員在 CMS 一律以「小編」送出，若要以藝人本人身分發送，請由藝人帳號在創作者工作區或 App 送出。
          </p>
        )}
        {config.role === "editor" && <p className="text-xs text-muted-foreground">你是小編，訊息會標示「小編」。</p>}
      </div>

      <div className="space-y-1">
        <Label htmlFor={`dm-text-${artistId}`}>
          {type === "poll" ? "投票說明文字" : type === "text" ? "訊息內容" : "說明文字（選填）"}（可插入 {"{name}"}）
        </Label>
        <Textarea id={`dm-text-${artistId}`} rows={4} value={text} disabled={send.isPending} onChange={(e) => setText(e.target.value)} />
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Button size="sm" variant="ghost" type="button" onClick={() => setText((t) => `${t}{name}`)}>
            插入 {"{name}"}
          </Button>
          <span className={textLen > textMax ? "text-destructive" : "text-muted-foreground"}>
            {textLen}／{textMax.toLocaleString()} 字
          </span>
        </div>
        {text && (
          <p className="rounded bg-muted p-2 text-sm">
            <span className="text-xs text-muted-foreground">粉絲看到（以「{fanName}」為例）：</span>
            <span className="block whitespace-pre-wrap break-words">{previewName(text, fanName)}</span>
          </p>
        )}
      </div>

      {type === "image" && (
        <div className="space-y-2">
          <Label htmlFor={`dm-images-${artistId}`}>
            圖片（1–{max} 張，每張 ≤8MB）
          </Label>
          <Input
            id={`dm-images-${artistId}`}
            type="file"
            accept="image/*"
            multiple
            disabled={send.isPending || uploads.length >= max}
            onChange={(e) => {
              void addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div className="flex flex-wrap gap-2">
            {uploads.map((u) => (
              <div key={u.assetId} className="relative">
                <img src={u.previewUrl} alt="" className={`h-20 w-20 rounded object-cover ${u.status !== "ready" ? "opacity-50" : ""}`} />
                <span className="block text-center text-xs">{u.status === "uploading" ? "上傳中…" : u.status === "failed" ? "失敗" : "完成"}</span>
                {u.error && <span className="block max-w-[5rem] text-xs text-destructive">{u.error}</span>}
                <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => removeUpload(u.assetId)}>
                  移除
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {(type === "voice" || type === "video") && (
        <div className="space-y-2">
          <Label htmlFor={`dm-clip-${artistId}`}>
            {type === "voice" ? "語音（m4a，≤60 秒、≤2MB）" : "影片（mp4／mov，≤60 秒、≤200MB；上傳後自動轉檔）"}
          </Label>
          <Input
            id={`dm-clip-${artistId}`}
            type="file"
            accept={type === "voice" ? ".m4a,.aac,audio/mp4,audio/x-m4a,audio/aac" : "video/mp4,video/quicktime,.mp4,.mov"}
            disabled={send.isPending || clipBusy}
            onChange={(e) => {
              void pickClip(type, e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          {clip && clip.kind === type && (
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="max-w-[16rem] truncate">{clip.name}</span>
              {clip.durationMs != null && <span>{formatDuration(clip.durationMs)}</span>}
              {clip.status === "uploading" && <span>{clip.step}</span>}
              {clip.status === "ready" && (
                <Badge variant="success">{clip.kind === "video" ? videoStatusLabels[clip.videoStatus ?? ""] ?? "已上傳" : "完成"}</Badge>
              )}
              {clip.status === "failed" && <span className="text-destructive">{clip.error}</span>}
              {clip.status === "ready" && clip.kind === "video" && clip.videoStatus !== "ready" && (
                <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => void refreshClip()}>
                  更新轉檔狀態
                </Button>
              )}
              {!clipBusy && (
                <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => setClip(null)}>
                  移除
                </Button>
              )}
            </div>
          )}
          {type === "video" && (
            <p className="text-xs text-muted-foreground">
              不必等轉檔完成：轉檔中送出的訊息會先顯示「影片處理中」，完成後自動發送；超過 60 秒或轉檔失敗會標為「發送失敗」。
            </p>
          )}
        </div>
      )}

      {type === "poll" && (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label htmlFor={`dm-poll-question-${artistId}`}>投票題目（選填，≤{DM_POLL_QUESTION_MAX} 字）</Label>
            <Input
              id={`dm-poll-question-${artistId}`}
              value={question}
              placeholder="例：下次直播想聊什麼？"
              onChange={(e) => setQuestion(e.target.value)}
            />
            {codePoints(question) > DM_POLL_QUESTION_MAX && (
              <p className="text-xs text-destructive">
                {codePoints(question)}／{DM_POLL_QUESTION_MAX} 字
              </p>
            )}
          </div>
          <p className="text-sm font-medium">選項（2–4 個，各 ≤40 字，單選）</p>
          {options.map((o, i) => (
            <div key={i} className="flex gap-2">
              <Input
                aria-label={`選項 ${i + 1}`}
                value={o}
                maxLength={80}
                onChange={(e) => setOptions((all) => all.map((x, j) => (j === i ? e.target.value : x)))}
              />
              {options.length > 2 && (
                <Button size="sm" variant="ghost" onClick={() => setOptions((all) => all.filter((_, j) => j !== i))}>
                  刪除
                </Button>
              )}
            </div>
          ))}
          {options.length < 4 && (
            <Button size="sm" variant="outline" onClick={() => setOptions((all) => [...all, ""])}>
              新增選項
            </Button>
          )}
          <div>
            <Label htmlFor={`dm-poll-close-${artistId}`}>截止時間（選填，30 天內）</Label>
            <Input id={`dm-poll-close-${artistId}`} type="datetime-local" value={closesAt} onChange={(e) => setClosesAt(e.target.value)} />
          </div>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={schedule} onChange={(e) => setSchedule(e.target.checked)} />
          排程發送（1 分鐘後～7 天內）
        </label>
        {schedule && (
          <Input
            aria-label="排程時間"
            type="datetime-local"
            className="w-auto"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        )}
      </div>
      {schedule && <p className="text-xs text-muted-foreground">排程訊息到點由系統送出（需 dmScheduledSender 上線，B-31）；送出前可取消。</p>}

      {(formError || send.isError) && (
        <p role="alert" className="text-destructive">
          {formError || errorText(send.error)}
        </p>
      )}
      {!config.enabled && <p className="text-sm text-muted-foreground">私訊開關關閉中，請先開啟才能發送。</p>}
      <Button
        disabled={disabled}
        onClick={() => {
          setFormError("");
          const label = schedule ? "排程這則訊息" : `立即發送給所有鐵粉${senderKind === "team" ? "（以小編身分）" : ""}`;
          confirm({
            title: `確定${label}？`,
            message: schedule ? "到點由系統送出；送出前可取消。" : "送出後會出現在鐵粉的私訊中，之後只能收回。",
            confirmText: schedule ? "排程" : "發送",
            onConfirm: () => send.mutate(),
          });
        }}
      >
        {send.isPending ? "送出中…" : clipBusy ? "等待上傳…" : schedule ? "排程" : "發送"}
      </Button>
      {dialog}
    </div>
  );
}
